import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { DprActivityCustomDataGridData } from '@dpr-details/models/grid';
import {
  DateDisplayComponent,
  DateEditComponent,
  MandatoryDateComponent,
  MandatoryTextComponent,
  NumericEditComponent,
  SelectDisplayComponent,
  SelectEditComponent,
  TextDisplayComponent,
  TextEditComponent,
} from '@grid/components';
import { GridSortDirection, GridSortMode } from '@grid/enums';
import {
  GridApi,
  GridColumn,
  GridOptions,
  GridSync,
  NumericCellConfig,
} from '@grid/models';
import { Constants as GridConstants } from '@grid/utils/constants';
import { StaticMessage } from '@shared/enums';
import { DataType, SelectionItem } from '@shared/models';
import { GridBaseComponent } from '@shared/models/component';
import { DprActivityCustomData } from '@shared/models/dpr';
import {
  ActivityMobilisation,
  CustomData,
  CustomDataValue,
} from '@shared/models/project-setup';
import {
  Constants,
  buildBooleanValues,
  getStaticMessage,
  isConfigurationDataType,
} from '@shared/utils';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';

@Component({
  selector: 'app-dpr-edit-activity-custom-data-grid',
  templateUrl: './dpr-edit-activity-custom-data-grid.component.html',
  styleUrls: ['./dpr-edit-activity-custom-data-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprEditActivityCustomDataGridComponent
  extends GridBaseComponent<DprActivityCustomDataGridData>
  implements OnChanges
{
  //#region Templates

  @ViewChild('timeComponent', { static: true })
  timeComponent: TemplateRef<any>;

  //#endregion

  //#region Inputs

  @Input({ required: true }) customDataList: CustomData[];
  @Input({ required: true }) customDataListValues: CustomDataValue[];
  @Input() predefinedChanges: GridSync<DprActivityCustomData>;
  @Input() activityMobilisation: ActivityMobilisation[];
  @Input() activityId: number;
  @Input() dprLockedDate: Date;

  //#endregion

  gridEntityName = $localize`:@@dprDetails.dprEditActivityModal.dprEditActivityCustomDataGrid.txtCustomData:Custom Data`;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
      this.gridColumns$.next(this.getGridColumns());
    }
  }

  protected getGridColumns(): GridColumn<DprActivityCustomDataGridData>[] {
    return [
      {
        columnDef: 'name',
        header: {
          name: $localize`:@@dprDetails.dprEditActivityModal.dprEditActivityCustomDataGrid.colName:Name`,
        },
        display: { type: TextDisplayComponent },
        allowEditing: false,
        minWidth: 200,
        tabIndex: -1,
        disableFocusedBackground: true,
      },
      {
        columnDef: 'remarks',
        header: {
          name: $localize`:@@dprDetails.dprEditActivityModal.dprEditActivityCustomDataGrid.colRemarks:Remarks`,
        },
        display: (data: DprActivityCustomDataGridData) => {
          data.remarks = this.customDataList.find(
            (x) => x.id === data.customDataId
          ).remark;

          return {
            type: TextDisplayComponent,
          };
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowEditing: false,
        minWidth: 200,
      },
      {
        columnDef: 'value',
        header: {
          name: $localize`:@@dprDetails.dprEditActivityModal.dprEditActivityCustomDataGrid.colValue:Value`,
        },
        display: (data: DprActivityCustomDataGridData) => {
          if (isConfigurationDataType(data?.dataTypeId)) {
            if (data.value) {
              data.value = +data.value;
            }

            return {
              type: this.markAsMandatory(data)
                ? MandatoryTextComponent
                : SelectDisplayComponent,
              config: {
                options:
                  data?.dataTypeId === DataType.List
                    ? this.selectionItemsForData(data.customDataId)
                    : buildBooleanValues(data?.booleanTerminologyId).map(
                        (v) => ({ id: v.id, name: v.value })
                      ),
              },
            };
          }

          if (
            data.dataTypeId === DataType.Date ||
            data.dataTypeId === DataType.Datetime
          ) {
            return {
              type: this.markAsMandatory(data)
                ? MandatoryDateComponent
                : DateDisplayComponent,
              config: {
                justifyContentForDisplay: true,
                format:
                  data.dataTypeId === DataType.Datetime
                    ? GridConstants.DefaultValues.dateDisplayFormatWithTime
                    : undefined,
              },
            };
          }

          return {
            type: this.markAsMandatory(data)
              ? MandatoryTextComponent
              : TextDisplayComponent,
            config: {
              options: {
                allowPercentage: data.dataTypeId === DataType.Percentage,
              },
            },
          };
        },
        edit: (data: DprActivityCustomDataGridData) => {
          if (isConfigurationDataType(data?.dataTypeId)) {
            if (data.value) {
              data.value = +data.value;
            }

            return {
              type: SelectEditComponent,
              allowEditing: this.canEdit,
              config: {
                options:
                  data?.dataTypeId === DataType.List
                    ? this.selectionItemsForData(data.customDataId)
                    : buildBooleanValues(data?.booleanTerminologyId).map(
                        (v) => ({ id: v.id, name: v.value })
                      ),
                includeNoneOption: true,
                noneOptionName: getStaticMessage(StaticMessage.NoneOptionName),
              },
            };
          }

          if (data?.dataTypeId === DataType.Number) {
            const config: NumericCellConfig = {
              options: {
                allowSign: false,
                allowDecimals: true,
                step: 1 / Math.pow(10, data.decimalCount ?? 2),
                max: Constants.NumericOptions.maxActivitiesCustomData,
                decimalPrecision: data.decimalCount ?? 2,
                min: 0,
              },
            };

            return {
              type: NumericEditComponent,
              allowEditing: this.canEdit,
              config,
            };
          }

          if (data.dataTypeId === DataType.Percentage) {
            const config: NumericCellConfig = {
              options: {
                step: 0.01,
                decimalPrecision: 2,
                allowPercentage: true,
              },
            };

            return {
              type: NumericEditComponent,
              allowEditing: this.canEdit,
              config,
            };
          }

          if (data.dataTypeId === DataType.Date) {
            return {
              type: DateEditComponent,
              allowEditing: this.canEdit,
              config: {
                allowClearOption: true,
                clearButtonLabel: getStaticMessage(StaticMessage.ClearButton),
                justifyContentForDisplay: true,
                timeOptions: {
                  withoutTimeZone: true,
                },
              },
              delegateEndEdit: true,
            };
          }

          if (data.dataTypeId === DataType.Datetime) {
            return {
              type: DateEditComponent,
              delegateEndEdit: true,
              config: {
                saveButtonLabel: getStaticMessage(StaticMessage.SaveButton),
                clearButtonLabel: getStaticMessage(StaticMessage.ClearButton),
                timeOptions: {
                  allowTimePicker: true,
                  timePickerComponent: this.timeComponent,
                  withoutTimeZone: true,
                },
                justifyContentForDisplay: true,
                format: GridConstants.DefaultValues.dateDisplayFormatWithTime,
              },
            };
          }

          return {
            type: TextEditComponent,
            allowEditing: this.canEdit,
            config: {
              options: {
                maxLength: Constants.FieldLimits.maxFreeTextDataTypeLimit,
              },
            },
          };
        },
        tooltip: {
          message: (context) => {
            switch (context.data?.dataTypeId) {
              case DataType.List: {
                return this.selectionItemsForData(
                  context.data?.customDataId
                )?.find((x) => x.id === +context.data?.value)?.name;
              }
              case DataType.Boolean: {
                return buildBooleanValues(
                  context.data?.booleanTerminologyId
                )?.find((x) => x.id === +context.data?.value)?.value;
              }
              default: {
                return context.data.value as string;
              }
            }
          },
        },
        allowEditing: this.canEdit,
        minWidth: 200,
      },
      GridColumns.comment,
    ];
  }

  protected getGridOptions(): GridOptions<DprActivityCustomDataGridData> {
    return {
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: false,
        keepAutogeneratedIds: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: false,
      },
      saveConfig: {
        isExternalSave: true,
      },
      sortConfig: {
        mode: GridSortMode.Fixed,
        definition: [
          {
            columnDef: 'priority',
            direction: GridSortDirection.Ascending,
          },
        ],
      },
      headerConfig: {
        enableStickyDirective: false,
      },
      events: {
        onGridRegistered: (gridApi: GridApi<DprActivityCustomDataGridData>) => {
          this.gridRegistered.emit(gridApi);
        },
      },
    };
  }

  //#region Private methods

  private markAsMandatory(data: DprActivityCustomDataGridData): boolean {
    return (
      this.activityMobilisation?.find(
        (x) =>
          x.activityId === this.activityId &&
          x.customDataId === data.customDataId
      )?.isMandatory &&
      (data.value === undefined ||
        String.isNullOrWhiteSpace(data.value?.toString())) &&
      !this.dprLockedDate
    );
  }

  private selectionItemsForData(customDataId: number): SelectionItem[] {
    return this.customDataListValues
      .filter((cd) => cd.customDataId === customDataId)
      .map((data) => ({
        id: data.id,
        name: data.value,
      }));
  }

  //#endregion
}
